import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  fetchCompanyRuleQuota,
  updateCompanyRuleQuota,
} from '@/api/(admin)/automation-rule-quota';
import { useTranslator } from '@/hooks/useTranslator';

export function companyRuleQuotaQueryOptions(companyId: string) {
  return queryOptions({
    queryKey: ['admin', 'company-automation-rule-quota', companyId] as const,
    queryFn: () => fetchCompanyRuleQuota(companyId),
    enabled: Boolean(companyId),
  });
}

export function useCompanyRuleQuota(companyId: string) {
  const { getTranslation: t } = useTranslator({ page: 'managementCompanies' });
  const queryClient = useQueryClient();
  const query = useQuery(companyRuleQuotaQueryOptions(companyId));

  const mutation = useMutation({
    mutationFn: (maxRules: number) =>
      updateCompanyRuleQuota(companyId, { maxRules }),
  });

  async function save(maxRules: number): Promise<boolean> {
    try {
      const response = await mutation.mutateAsync(maxRules);
      queryClient.setQueryData(
        companyRuleQuotaQueryOptions(companyId).queryKey,
        response,
      );
      toast.success(t('detail.automationRules.saved'));
      return true;
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t('errors.automationRuleQuotaSaveFailed'),
      );
      return false;
    }
  }

  return {
    quota: query.data?.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    save,
    isSaving: mutation.isPending,
  };
}
